import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface SearchOverlayProps {
  open: boolean;
  initialValue?: string;
  onClose: () => void;
}

export default function SearchOverlay({ open, initialValue = "", onClose }: SearchOverlayProps) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, initialValue]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = () => {
    const keyword = value.trim();
    onClose();
    if (keyword) {
      navigate(`/search?q=${encodeURIComponent(keyword)}`);
    } else {
      navigate("/search");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-24 md:pt-32">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl animate-fade-up">
        <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-2">
          <span className="flex h-10 w-10 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-xl"></i>
          </span>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder="搜索影片、电视剧、综艺、演员…"
            className="h-10 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
          />
          <button
            type="button"
            onClick={submit}
            className="h-10 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
          >
            搜索
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span>热门搜索：</span>
          {["星海远征", "长安夜行", "科幻", "悬疑", "综艺"].map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => {
                onClose();
                navigate(`/search?q=${encodeURIComponent(tag)}`);
              }}
              className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-foreground-700 transition hover:border-primary-300 hover:text-primary-400"
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}